
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../../lib/supabase';
import { createNewsUrl } from '../../../../utils/urlUtils';

interface Article {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  image: string;
  category: string;
  published_at: string;
  read_time: number;
  views: number;
  tags: string[];
  author_name: string;
  author_avatar: string;
  is_featured: boolean;
  tagged_creators: string[];
}

interface MentionedInArticlesProps {
  creatorId: string;
  creatorName: string;
  creatorSlug?: string;
  variant?: 'sidebar' | 'inline';
}

const CATEGORIES: Record<
  string,
  { name: string; icon: string; color: string; bg: string }
> = {
  production: {
    name: 'Production',
    icon: 'ri-film-line',
    color: 'text-amber-600',
    bg: 'bg-amber-100',
  },
  streaming: {
    name: 'Streaming',
    icon: 'ri-live-line',
    color: 'text-orange-600',
    bg: 'bg-orange-100',
  },
  awards: {
    name: 'Awards',
    icon: 'ri-award-line',
    color: 'text-yellow-600',
    bg: 'bg-yellow-100',
  },
  casting: {
    name: 'Casting',
    icon: 'ri-user-star-line',
    color: 'text-rose-600',
    bg: 'bg-rose-100',
  },
  technology: {
    name: 'Technology',
    icon: 'ri-cpu-line',
    color: 'text-teal-600',
    bg: 'bg-teal-100',
  },
  industry: {
    name: 'Industry',
    icon: 'ri-building-line',
    color: 'text-indigo-600',
    bg: 'bg-indigo-100',
  },
};

const formatDate = (d: string) =>
  new Date(d).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

const formatViews = (n: number) => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
};

export default function MentionedInArticles({
  creatorId,
  creatorName,
  creatorSlug,
  variant = 'sidebar',
}: MentionedInArticlesProps) {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const { data, error } = await supabase
          .from('news_articles')
          .select(
            'id, title, slug, excerpt, image, category, published_at, read_time, views, tags, author_name, author_avatar, is_featured, tagged_creators'
          )
          .eq('status', 'published')
          .order('published_at', { ascending: false });

        if (error) throw error;
        if (!data) {
          setLoading(false);
          return;
        }

        const lowerName = creatorName.toLowerCase();

        const matched = data.filter((article) => {
          const taggedIds = article.tagged_creators || [];
          if (taggedIds.includes(creatorId)) return true;
          if (creatorSlug && taggedIds.includes(creatorSlug)) return true;
          const titleLower = (article.title || '').toLowerCase();
          if (titleLower.includes(lowerName)) return true;
          const articleTags = (article.tags || []).map((t) => t.toLowerCase());
          if (
            articleTags.some(
              (t) => t.includes(lowerName) || lowerName.includes(t)
            )
          )
            return true;
          return false;
        });

        matched.sort((a, b) => {
          const aTagged = (a.tagged_creators || []).includes(creatorId)
            ? 1
            : 0;
          const bTagged = (b.tagged_creators || []).includes(creatorId)
            ? 1
            : 0;
          if (bTagged !== aTagged) return bTagged - aTagged;
          return (
            new Date(b.published_at).getTime() -
            new Date(a.published_at).getTime()
          );
        });

        setArticles(matched);
      } catch (err) {
        console.error('Error fetching mentioned articles:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, [creatorId, creatorName, creatorSlug]);

  if (loading) {
    return (
      <div
        className={
          variant === 'inline'
            ? 'pt-2'
            : 'bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8'
        }
      >
        <div className="flex items-center gap-3 mb-5">
          <div className="w-36 h-5 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="space-y-4">
          {[1, 2].map((i) => (
            <div key={i} className="flex gap-4 animate-pulse">
              <div className="w-28 h-20 bg-gray-200 rounded-lg flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="w-3/4 h-4 bg-gray-200 rounded" />
                <div className="w-1/2 h-3 bg-gray-100 rounded" />
                <div className="w-1/3 h-3 bg-gray-100 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (articles.length === 0) return null;

  const displayLimit = variant === 'inline' ? 4 : 3;
  const displayed = showAll ? articles : articles.slice(0, displayLimit);

  return (
    <div
      className={
        variant === 'inline'
          ? 'pt-2'
          : 'bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8'
      }
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
            <i className="ri-newspaper-line text-white text-sm"></i>
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">Mentioned In</h3>
            <p className="text-xs text-gray-500">
              {articles.length} article{articles.length !== 1 ? 's' : ''}{' '}
              referencing {creatorName.split(' ')[0]}
            </p>
          </div>
        </div>
        <Link
          to="/news"
          className="text-sm text-amber-600 hover:text-amber-700 font-medium flex items-center gap-1 cursor-pointer"
        >
          All News
          <i className="ri-arrow-right-line"></i>
        </Link>
      </div>

      <div
        className={
          variant === 'inline'
            ? 'grid grid-cols-1 md:grid-cols-2 gap-4'
            : 'space-y-4'
        }
      >
        {displayed.map((article) => {
          const cat = CATEGORIES[article.category] || CATEGORIES.production;
          const isExplicitlyTagged = (article.tagged_creators || []).includes(
            creatorId
          );

          return (
            <Link
              key={article.id}
              to={`/news/${article.slug}`}
              className={`group flex gap-4 p-3 rounded-xl hover:bg-amber-50/60 transition-all ${
                variant === 'inline'
                  ? 'border border-gray-100 hover:border-amber-200'
                  : '-mx-3'
              }`}
            >
              {/* Thumbnail */}
              <div className="relative w-28 h-20 flex-shrink-0 rounded-lg overflow-hidden">
                <img
                  src={article.image ?? ''}
                  alt={article.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
                {isExplicitlyTagged && (
                  <div
                    className="absolute top-1 left-1 w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center"
                    title="Explicitly tagged"
                  >
                    <i className="ri-link text-white text-xs"></i>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className={`px-2 py-0.5 ${cat.bg} ${cat.color} text-[10px] font-medium rounded-full`}
                  >
                    {cat.name}
                  </span>
                  {article.is_featured && (
                    <span className="px-1.5 py-0.5 bg-amber-100 text-amber-600 text-[10px] font-medium rounded-full">
                      <i className="ri-star-fill mr-0.5"></i>Featured
                    </span>
                  )}
                </div>
                <h4 className="font-semibold text-gray-900 text-sm line-clamp-2 group-hover:text-amber-600 transition-colors leading-snug">
                  {article.title}
                </h4>
                <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {formatDate(article.published_at)}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {article.read_time} min
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {formatViews(article.views ?? 0)}
                  </span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {articles.length > displayLimit && (
        <button
          onClick={(e) => {
            e.preventDefault();
            setShowAll(!showAll);
          }}
          className="mt-4 w-full py-2.5 text-sm font-medium text-amber-600 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
        >
          {showAll ? (
            <>
              <i className="ri-arrow-up-s-line mr-1"></i>Show Less
            </>
          ) : (
            <>
              <i className="ri-arrow-down-s-line mr-1"></i>View All{' '}
              {articles.length} Articles
            </>
          )}
        </button>
      )}
    </div>
  );
}
